import type { MouseEvent, ReactNode } from "react";
import { Button } from "@mantine/core";
import { useLayout } from "./useLayout";

/**
 * The pill. Filters, zoom presets, admin tabs, nav links, modal actions, the live control.
 *
 * A component rather than a class string, because the class string was the problem: applied by
 * hand at two dozen call sites, each one decided independently whether a chip was a button, a
 * link, a toggle or an inert span, and appearance stopped predicting behaviour. The page ended
 * up with a red pill that looked exactly as pressable as the teal one beside it and did
 * nothing, which is a footgun that only exists because nothing forced the two to differ.
 *
 * So: a `Chip` is always interactive. If it does not do anything it is not a chip, it is a
 * `StatePill`, which lives next door and cannot take an onClick.
 *
 * A Mantine `Button` underneath, with the same prop union it had when it was a hand-styled
 * element: sixty call sites and the whole variant vocabulary stay where they were, and only the
 * pixels move.
 */

export type ChipVariant = "accent" | "danger" | "warning" | "success" | "admin";

interface ChipCommon {
  variant?: ChipVariant;
  /**
   * A toggle that is currently on. Draws the lit state and reports `aria-pressed`, which is the
   * half that is easy to miss: a filter that looks selected and announces nothing is selected
   * to nobody using a screen reader.
   */
  pressed?: boolean;
  /**
   * The pill shape, for the one chip that swaps slots with a `StatePill`: a control whose
   * outline changed with its state would read as two different controls. Everything else
   * takes the theme's `sm`.
   */
  radius?: string | number;
  title?: string;
  "aria-label"?: string;
  /** A mark before the label. Mantine's own slot, so spacing and alignment are not ours. */
  leftSection?: ReactNode;
  children: ReactNode;
}

interface ChipButton extends ChipCommon {
  as?: "button";
  type?: "button" | "submit";
  onClick?: () => void;
  disabled?: boolean;
}

interface ChipLink extends ChipCommon {
  as: "link";
  href: string;
  onClick?: (event: MouseEvent) => void;
}

export type ChipProps = ChipButton | ChipLink;

/**
 * The variant vocabulary.
 *
 * `accent` and `danger` do not look at `pressed`: their own colour already says what they are,
 * and a lit state on top of it would be a second distinction carrying nothing new. Admin is the
 * one pair that needs both -- the generic lit border is teal and the admin chip's whole point
 * is that it is not.
 *
 * Colour and fill only. Weight is the theme's, set once on `--mantine-font-weight-medium`:
 * these used to bump three of the four variants to 600, which is how the toolbar ended up with
 * bold chips beside regular ones for no reason a reader could name.
 */
function look(variant: ChipVariant | undefined, pressed: boolean | undefined) {
  switch (variant) {
    case "accent":
      return { variant: "filled", color: "accent" } as const;
    case "danger":
      return { variant: "outline", color: "danger" } as const;
    case "warning":
      return { variant: "light", color: "warn" } as const;
    case "success":
      return { variant: "light", color: "jukebox" } as const;
    case "admin":
      return pressed
        ? ({ variant: "light", color: "admin" } as const)
        : ({ variant: "outline", color: "admin" } as const);
    default:
      return pressed ? ({ variant: "light", color: "accent" } as const) : ({} as const);
  }
}

export function Chip(props: ChipProps) {
  const { variant, pressed, title, leftSection, children } = props;
  const label = props["aria-label"];
  // A finger is not a pointer: a chip is 26px tall, which is fine to click and small to tap.
  // Called here rather than widened into the prop union -- there are sixty call sites.
  const { coarse } = useLayout();

  if (props.as === "link") {
    return (
      <Button
        component="a"
        mih={coarse ? 36 : undefined}
        radius={props.radius}
        href={props.href}
        onClick={props.onClick}
        leftSection={leftSection}
        title={title}
        aria-label={label}
        // Not `aria-pressed`: a link is not a toggle button, and the attribute is ignored on
        // one. `aria-current` is how a link says "this is the page you are on", which is the
        // same fact the lit state is drawing.
        aria-current={pressed ? "page" : undefined}
        {...look(variant, pressed)}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button
      // Never left to default to "submit": a chip inside a form that quietly submitted it is
      // the other half of the same appearance-does-not-predict-behaviour bug.
      type={props.type ?? "button"}
      mih={coarse ? 36 : undefined}
      radius={props.radius}
      onClick={props.onClick}
      leftSection={leftSection}
      disabled={props.disabled}
      title={title}
      aria-label={label}
      // Only on things that are actually toggles. `undefined` leaves the attribute off, which
      // is different from `false`: a plain button is not an unpressed toggle.
      aria-pressed={pressed === undefined ? undefined : pressed}
      {...look(variant, pressed)}
    >
      {children}
    </Button>
  );
}
